import { useEffect } from "react";

/**
 * WCAG 2.1.1: a region that scrolls horizontally (wide tables, diagrams)
 * must be reachable by keyboard. After each route render, every overflowing
 * `overflow-x-auto` / `overflow-auto` element without focusable content gets
 * tabIndex 0 and a region label, so arrow keys can scroll it.
 */
export function useKeyboardScrollRegions(routeKey: string, label: string) {
  useEffect(() => {
    const mark = () => {
      document
        .querySelectorAll<HTMLElement>(".overflow-x-auto, .overflow-auto")
        .forEach((el) => {
          if (el.hasAttribute("tabindex")) return;
          if (el.scrollWidth <= el.clientWidth + 1) return;
          if (
            el.querySelector(
              "a[href], button, input, select, textarea, [tabindex]",
            )
          )
            return;
          el.tabIndex = 0;
          if (!el.hasAttribute("role")) el.setAttribute("role", "region");
          if (!el.hasAttribute("aria-label"))
            el.setAttribute("aria-label", label);
        });
    };
    const frame = requestAnimationFrame(mark);
    const timer = setTimeout(mark, 1500);
    window.addEventListener("resize", mark);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      window.removeEventListener("resize", mark);
    };
  }, [routeKey, label]);
}
